import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Plus } from "lucide-react";

const inputCls = "w-full bg-surface border border-ink/20 focus:border-ember outline-none px-3 py-2.5 text-ink placeholder:text-ink/30 transition-colors text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-ink/50 mb-1.5";

const METHODS = [
  ["moneygram", "MoneyGram"],
  ["western_union", "Western Union"],
  ["other", "Autre"],
];

const STATUS = {
  pending: ["En attente", "text-amber-600"],
  confirmed: ["Confirmé", "text-green-600"],
  rejected: ["Refusé", "text-red-500"],
};

export default function AdminPayments({ onChanged }) {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ user_id: "", course_id: "", amount: "109", method: "moneygram", reference: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const [p, u, c] = await Promise.all([
        api.get("/admin/payments"),
        api.get("/admin/users"),
        api.get("/admin/courses"),
      ]);
      setPayments(p.data);
      setUsers(u.data);
      setCourses(c.data);
      setForm((f) => ({
        ...f,
        user_id: f.user_id || u.data[0]?.id || "",
        course_id: f.course_id || c.data[0]?.id || "",
        amount: f.amount || String(c.data[0]?.price || 109),
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const create = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/payments", { ...form, amount: parseFloat(form.amount) || 0 });
      setShowForm(false);
      setForm((f) => ({ ...f, reference: "", notes: "" }));
    }, "Paiement enregistré");
  };

  const confirm = (id) => {
    if (!window.confirm("Confirmer ce paiement ? L'accès à la formation sera activé automatiquement et un email sera envoyé au client.")) return;
    run(async () => {
      const { data } = await api.post(`/admin/payments/${id}/confirm`);
      toast.success(data.email_sent ? "Paiement confirmé — accès activé, email envoyé au client" : "Paiement confirmé — accès activé (email non envoyé)");
    });
  };

  const reject = (id) => {
    if (!window.confirm("Refuser ce paiement ? Aucun accès ne sera accordé.")) return;
    run(() => api.post(`/admin/payments/${id}/reject`), "Paiement refusé");
  };

  const methodLabel = (m) => METHODS.find(([k]) => k === m)?.[1] || m;

  return (
    <div className="mt-10" data-testid="admin-payments">
      <div className="flex justify-end mb-5">
        <button data-testid="new-payment-btn" onClick={() => setShowForm(!showForm)} className="btn-glow bg-ink hover:bg-ember text-white px-5 py-3 text-sm flex items-center gap-2 transition-colors">
          <Plus size={14} /> Enregistrer un paiement
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="mb-8 border border-ink/10 bg-surface p-6 grid md:grid-cols-3 gap-4" data-testid="payment-form">
          <div>
            <label className={labelCls}>Client</label>
            <select data-testid="payment-user" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required className={inputCls}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Formation</label>
            <select data-testid="payment-course" value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} className={inputCls}>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title_fr}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Montant (€)</label>
            <input data-testid="payment-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Moyen de paiement</label>
            <select data-testid="payment-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className={inputCls}>
              {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Référence de paiement</label>
            <input data-testid="payment-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="N° de transaction…" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <input data-testid="payment-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          </div>
          <div className="md:col-span-3">
            <button disabled={busy} className="btn-glow bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-3 text-sm transition-colors" data-testid="payment-submit">Enregistrer le paiement</button>
          </div>
        </form>
      )}

      <div className="border border-ink/10 bg-surface overflow-x-auto" data-testid="payments-table">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-ink/10 text-[11px] uppercase tracking-[0.2em] text-ink/40">
            <span className="col-span-2">Date</span>
            <span className="col-span-3">Client</span>
            <span className="col-span-2">Formation</span>
            <span className="col-span-1">Montant</span>
            <span className="col-span-1">Moyen</span>
            <span className="col-span-1">Statut</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {payments.length === 0 && <p className="px-6 py-10 text-ink/40 text-sm">Aucun paiement manuel enregistré.</p>}
          {payments.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-ink/5 text-sm items-center" data-testid={`payment-row-${p.id}`}>
              <span className="col-span-2 text-ink/45 text-xs">{new Date(p.created_at).toLocaleDateString()}</span>
              <div className="col-span-3 min-w-0">
                <p className="text-ink text-sm truncate">{p.user_name || "—"}</p>
                <p className="text-ink/45 text-xs truncate">{p.user_email}</p>
              </div>
              <span className="col-span-2 text-ink/60 text-xs truncate">{p.course_title}</span>
              <span className="col-span-1 font-serif italic text-lg text-ember">{p.amount}€</span>
              <span className="col-span-1 text-xs text-ink/60">{methodLabel(p.method)}</span>
              <span className={`col-span-1 text-xs font-medium ${STATUS[p.status]?.[1]}`}>{STATUS[p.status]?.[0] || p.status}</span>
              <div className="col-span-2 flex justify-end gap-2">
                {p.status === "pending" && (
                  <>
                    <button onClick={() => confirm(p.id)} data-testid={`payment-confirm-${p.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-green-500 text-green-600 hover:bg-green-500 hover:text-white transition-colors"><Check size={12} /> Confirmer</button>
                    <button onClick={() => reject(p.id)} data-testid={`payment-reject-${p.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-red-300 text-red-500 hover:bg-red-50 transition-colors"><X size={12} /> Refuser</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
